/**
 * CR310: Snippet Coverage Widget
 * Shows coverage of flyer snippet artifacts using the get_snippet_coverage RPC.
 * Distinguishes: has_snippet, virtual_ready, missing_inputs, failed.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Crop, Loader2, CheckCircle2, AlertTriangle, ImageOff, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

interface SnippetCoverage {
  total: number;
  has_snippet: number;
  virtual_ready: number;
  missing_inputs: number;
  missing_bbox: number;
  missing_page_image: number;
  failed: number;
}

export function SnippetCoverageWidget({ batchRunId }: { batchRunId?: string }) {
  const [coverage, setCoverage] = useState<SnippetCoverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastResult, setLastResult] = useState<{
    processed: number;
    failed: number;
  } | null>(null);

  const loadCoverage = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await (supabase as any).rpc("get_snippet_coverage", {
        p_batch_run_id: batchRunId ?? null,
      });

      if (error) {
        console.error("[SnippetCoverage] RPC error:", error);
        setLoading(false);
        return;
      }

      setCoverage(data as SnippetCoverage);
    } catch (err) {
      console.error("[SnippetCoverage] Error:", err);
    } finally {
      setLoading(false);
    }
  }, [batchRunId]);

  useEffect(() => {
    loadCoverage();
  }, [loadCoverage]);

  const handleGenerate = async () => {
    setGenerating(true);
    setLastResult(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Authentication required");
        return;
      }

      const body: Record<string, unknown> = batchRunId
        ? { batch_run_id: batchRunId, limit: 200, enrich_canonical: true }
        : { mode: "backfill", limit: 200 };

      const { data, error } = await supabase.functions.invoke(
        "batch-run-flyer-snippet-crop",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${session.access_token}` },
          body,
        }
      );

      if (error) {
        toast.error(`Snippet generation failed: ${error.message}`);
      } else {
        const result = data as { processed?: number; failed?: number; skipped?: number };
        setLastResult({
          processed: result.processed ?? 0,
          failed: result.failed ?? 0,
        });
        toast.success(
          `Generated ${result.processed ?? 0} snippets${
            result.failed ? `, ${result.failed} failed` : ""
          }`
        );
        await loadCoverage();
      }
    } catch (err) {
      toast.error(`Error: ${String(err)}`);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading snippet coverage…
      </div>
    );
  }

  if (!coverage || coverage.total === 0) return null;

  const coveragePct = ((coverage.has_snippet / coverage.total) * 100).toFixed(1);
  const canGenerate = coverage.virtual_ready > 0 || coverage.failed > 0;

  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3 flex flex-col gap-2 text-xs">
      {/* Header row */}
      <div className="flex items-center gap-2">
        <Crop className="h-4 w-4 text-muted-foreground" />
        <div>
          <div className="font-medium text-sm text-foreground">Snippet Coverage</div>
          <div className="text-muted-foreground">
            {coverage.has_snippet} of {coverage.total} flyer deals · {coveragePct}%
          </div>
        </div>
      </div>

      {/* Status badges */}
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          {coverage.has_snippet} cropped
        </span>
        {coverage.virtual_ready > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                <Layers className="h-3 w-3 mr-1" />
                {coverage.virtual_ready} ready to generate
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs max-w-xs">
              These deals have bbox + page image but no cropped snippet yet. Click "Generate" to create them.
            </TooltipContent>
          </Tooltip>
        )}
        {coverage.missing_inputs > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                <ImageOff className="h-3 w-3 mr-1" />
                {coverage.missing_inputs} missing inputs
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs max-w-xs">
              Cannot generate: {coverage.missing_bbox > 0 ? `${coverage.missing_bbox} missing bbox` : ""}
              {coverage.missing_bbox > 0 && coverage.missing_page_image > 0 ? ", " : ""}
              {coverage.missing_page_image > 0 ? `${coverage.missing_page_image} missing page image` : ""}
            </TooltipContent>
          </Tooltip>
        )}
        {coverage.failed > 0 && (
          <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {coverage.failed} failed
          </span>
        )}
      </div>

      {/* Last result + Generate button */}
      <div className="flex items-center gap-2">
        {lastResult && (
          <span className="text-[11px] text-muted-foreground">
            Last run: {lastResult.processed} created, {lastResult.failed} failed
          </span>
        )}

        {canGenerate && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerate}
            disabled={generating}
            className="ml-auto text-xs"
          >
            {generating ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                Generating…
              </>
            ) : (
              "Generate Missing Snippets"
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
